import { X } from 'lucide-react';
import { useEffect } from 'react';
import {
  resolveReferralCodeListStatus,
  type PanelReferralCodeDto,
  type PanelReferralCodeRedemptionDto,
} from '@icebreaker/shared';
import {
  Avatar,
  Button,
  dialogStyles,
  EmptyState,
  Modal,
  OrgMark,
  SectionHeading,
  Skeleton,
  Tag,
  TruncatedText,
} from '@icebreaker/ui';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import {
  REFERRAL_STATUS_VARIANT,
  redeemerName,
  redemptionDateText,
  remainingUses,
  usageText,
  validityText,
} from '../model/referral-code-view';
import { fetchReferralCodes, fetchReferralRedemptions } from '../store/referral-codes.slice';
import styles from './referral-codes.module.css';

/**
 * KULLANIM DETAYI (P7 · Story 6): sağdan açılan 480 px kenar katmanı.
 *
 * Özet (kod, durum, hediye, geçerlilik, kullanım/tavan, kalan, iç not) liste
 * verisinden çizilir. İletişim bilgisi yoktur.
 *
 * KULLANIMLAR (Story 6) katman her açıldığında sunucudan okunur
 * (`GET /panel/referral-codes/:id/redemptions`, saga üzerinden). Dört hâl:
 * yükleniyor (kurum iskeleti), hata (+ Yeniden dene), boş (sunucunun döndüğü
 * gerçek 0, `EmptyState`), dolu (kurum köşeli logo/monogram + ad + hediye,
 * altında yükleyen personel daire avatar + ad + tarih).
 */
export function ReferralCodeDrawer({
  id,
  now,
  editing,
  onClose,
  onEdit,
}: {
  id: string;
  now: Date;
  /** Üstte düzenleme diyaloğu açıkken katman Esc ile kapanmaz. */
  editing: boolean;
  onClose: () => void;
  onEdit: (code: PanelReferralCodeDto) => void;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const list = useAppSelector((root: RootState) => root.panelReferral.list);
  const code = list.items.find((item) => item.id === id) ?? null;
  const status = code ? resolveReferralCodeListStatus(code, now) : null;
  const remaining = code ? remainingUses(code) : null;

  useEffect(() => {
    dispatch(fetchReferralRedemptions.request({ id }));
  }, [dispatch, id]);

  const header = (
    <header className={styles.drawerHead}>
      <div className={styles.drawerHeadText}>
        <p className={[dialogStyles.modalKicker, styles.kickerSlot].join(' ')}>
          {t.t('admin.referralCodes.title')}
        </p>
        {code && status ? (
          <>
            <h2 className={styles.drawerCode}>{code.name}</h2>
            <Tag variant={REFERRAL_STATUS_VARIANT[status]}>
              {t.t(`admin.referralCodes.statuses.${status}`)}
            </Tag>
          </>
        ) : (
          <h2 className={styles.drawerCode}>{t.t('admin.referralCodes.drawer.label')}</h2>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className={styles.closeButton}
        aria-label={t.t('admin.referralCodes.drawer.close')}
        onClick={onClose}
      >
        <X size={18} aria-hidden="true" />
      </Button>
    </header>
  );

  return (
    <Modal
      open
      placement="side-wide"
      title={code?.name ?? t.t('admin.referralCodes.drawer.label')}
      closeLabel={t.t('admin.referralCodes.drawer.close')}
      header={header}
      onClose={onClose}
      closeDisabled={editing}
      footer={
        code ? (
          <div className={styles.drawerFoot}>
            <p className={styles.onceNote}>{t.t('admin.referralCodes.drawer.once')}</p>
            <Button variant="secondary" onClick={() => onEdit(code)}>
              {t.t('admin.referralCodes.edit')}
            </Button>
          </div>
        ) : undefined
      }
    >
      {code ? (
        <>
          <div className={styles.summary}>
            <dl className={styles.summaryGrid}>
              <div>
                <dt className={styles.summaryKey}>{t.t('admin.referralCodes.drawer.gift')}</dt>
                <dd className={styles.summaryValue}>
                  {t.t('admin.referralCodes.gift', { count: code.giftCredits })}
                </dd>
              </div>
              <div>
                <dt className={styles.summaryKey}>{t.t('admin.referralCodes.drawer.valid')}</dt>
                <dd className={styles.summaryValue}>{validityText(t, code)}</dd>
              </div>
              <div>
                <dt className={styles.summaryKey}>{t.t('admin.referralCodes.drawer.usage')}</dt>
                <dd className={styles.summaryValue}>{usageText(t, code)}</dd>
              </div>
            </dl>
            {code.usageCap !== null ? (
              <div className={styles.meter} aria-hidden="true">
                <div
                  className={styles.meterFill}
                  style={{ width: `${Math.min(100, (code.usageCount / code.usageCap) * 100)}%` }}
                />
              </div>
            ) : null}
            <p className={styles.capLine}>
              {remaining === null
                ? t.t('admin.referralCodes.drawer.noCap')
                : t.t('admin.referralCodes.drawer.remaining', { count: remaining })}
            </p>
            {code.note ? (
              <p className={styles.noteLine}>
                <span className={styles.noteKey}>{t.t('admin.referralCodes.drawer.note')}:</span>{' '}
                {code.note}
              </p>
            ) : null}
          </div>
          <div className={styles.usesSection}>
            <SectionHeading title={t.t('admin.referralCodes.drawer.uses')} />
            <RedemptionList id={id} />
          </div>
        </>
      ) : list.phase === 'failed' && !list.loaded ? (
        <div className={styles.drawerState} role="alert">
          <p className={styles.errorBanner}>{t.t('admin.referralCodes.listError')}</p>
          <Button variant="secondary" onClick={() => dispatch(fetchReferralCodes.request())}>
            {t.t('admin.referralCodes.retry')}
          </Button>
        </div>
      ) : list.loaded ? (
        <div className={styles.drawerState} role="status">
          <p className={styles.lockNote}>{t.t('admin.referralCodes.drawer.notFound')}</p>
          <Button variant="primary" onClick={onClose}>
            {t.t('admin.referralCodes.drawer.backToList')}
          </Button>
        </div>
      ) : (
        <Skeleton label={t.t('admin.referralCodes.loading')} lines={4} />
      )}
    </Modal>
  );
}

/** Kenar katmanın "Kullanımlar" gövdesi: dört hâl, açık kodun okumasından. */
function RedemptionList({ id }: { id: string }) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const state = useAppSelector((root: RootState) => root.panelReferral.redemptions);
  const current = state.codeId === id;

  if (current && state.phase === 'failed') {
    return (
      <div className={styles.drawerState} role="alert">
        <p className={styles.errorBanner}>{t.t('admin.referralCodes.drawer.usesError')}</p>
        <Button
          variant="secondary"
          onClick={() => dispatch(fetchReferralRedemptions.request({ id }))}
        >
          {t.t('admin.referralCodes.retry')}
        </Button>
      </div>
    );
  }
  if (!current || state.phase !== 'succeeded') {
    return (
      <Skeleton
        variant="entities"
        lines={3}
        label={t.t('admin.referralCodes.drawer.usesLoading')}
      />
    );
  }
  if (state.items.length === 0) {
    return (
      <EmptyState
        title={t.t('admin.referralCodes.drawer.emptyTitle')}
        body={t.t('admin.referralCodes.drawer.emptyBody')}
      />
    );
  }
  return (
    <div className={styles.uses}>
      <p className={styles.usesHead}>
        <span>{t.t('admin.referralCodes.drawer.by')}</span>
        <span className={styles.usesHeadDate}>{t.t('admin.referralCodes.drawer.usedAt')}</span>
      </p>
      <ul className={styles.usesList}>
        {state.items.map((item) => (
          <RedemptionRow key={item.id} item={item} />
        ))}
      </ul>
    </div>
  );
}

function RedemptionRow({ item }: { item: PanelReferralCodeRedemptionDto }) {
  const t = useTranslation();
  const staff = redeemerName(t, item);
  return (
    <li className={styles.useRow}>
      <div className={styles.useOrgLine}>
        <OrgMark
          name={item.organization.name}
          logoUrl={item.organization.logoUrl}
          fill={item.organization.brandFill}
          size="sm"
        />
        <TruncatedText as="b" className={styles.useOrgName}>
          {item.organization.name}
        </TruncatedText>
        <span className={styles.useGift}>
          {t.t('admin.referralCodes.gift', { count: item.giftCredits })}
        </span>
      </div>
      <div className={styles.useStaffLine}>
        <Avatar name={staff} size="xs" />
        <span className={styles.useStaffName}>{staff}</span>
        <time className={styles.useDate} dateTime={item.redeemedAt}>
          {redemptionDateText(t, item.redeemedAt)}
        </time>
      </div>
    </li>
  );
}
